import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Button, Icon, TextField } from '@bloo-team/ui'
import { Plus, Trash2 } from '@bloo-team/ui/icons'
import { ConfirmStrip } from '../../../components/settings/ConfirmStrip'
import { Pagination } from '@bloo-team/ui'
import { TokenForm } from '../../../components/settings/TokenForm'
import { TokenSecret } from '../../../components/settings/TokenSecret'
import { findCluster, useSession } from '../../../lib/session'
import {
  issuableScopes,
  useCreateToken,
  useDeleteToken,
  useTokens,
  type ApiToken,
} from '../../../lib/tokens'

/**
 * Settings · Tokens (docs/settings-requirements.md §3).
 *
 * The machine identities for this cluster. Three things this screen has to be
 * honest about, because the API is:
 *
 *   once     the secret exists in the UI for one panel and is never readable
 *            again — there is no read endpoint and no list field for it.
 *   no age   a row carries no created-at, last-used, or expiry (gap G2), so
 *            the screen cannot advise rotation and does not pretend to.
 *   no edit  there is no update endpoint (gap G3). Changing a token's
 *            capabilities means deleting it and issuing a new secret.
 *
 * Paging and search are SERVER-side: `count` in the envelope is the total
 * across all pages, so the footer counts from it and not from what is on
 * screen.
 */

function TokensPage() {
  const { clusterId, scopeId } = Route.useParams()
  const session = useSession()
  const cluster = session ? findCluster(session, clusterId) : undefined
  // Memoised: the form resets its draft when its inputs change identity, and
  // issuableScopes builds a new array every render.
  const scopes = useMemo(() => issuableScopes(cluster), [cluster])

  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [pageNo, setPageNo] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [formOpen, setFormOpen] = useState(false)
  const [secret, setSecret] = useState<{ value: string; appName: string } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<ApiToken | null>(null)

  // The filter runs SERVER-side, so every keystroke would be its own POST.
  // Settle first: "grafana" is one request, not seven.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search), 250)
    return () => clearTimeout(t)
  }, [search])

  const list = useTokens({ clusterId, scopeId, pageNo, pageSize, search: query })
  const create = useCreateToken()
  const remove = useDeleteToken()

  const rows = list.data?.tokens ?? []
  const total = list.data?.total ?? 0

  // A new search invalidates the page number: page 4 of the old result set is
  // not page 4 of the new one, and a stale page number shows an empty table
  // over a non-empty result.
  const onSearch = (value: string) => {
    setSearch(value)
    setPageNo(1)
  }

  return (
    <div className="app-stack app-stack-wide">
      <section className="hud-panel sy-card" aria-label="API tokens">
        <div className="sy-pane-head">
          <span className="hud-eyebrow">API tokens</span>
          <span className="mono muted">{total}</span>
          <Button variant="outline" size="sm" onPress={() => { create.reset(); setFormOpen(true) }}>
            <Icon icon={Plus} size={13} /> Issue token
          </Button>
        </div>

        <div className="sy-toolbar">
          <TextField
            aria-label="Search by app name"
            value={search}
            onChange={onSearch}
            placeholder="Search app name…"
          />
        </div>

        {list.error ? (
          <p className="sy-error" role="alert">
            {list.error.message}
          </p>
        ) : null}

        {!rows.length && !list.isLoading && !list.error ? (
          <p className="sy-help">
            {query
              ? `No token's app name matches “${query}”.`
              : 'No tokens issued in this scope. Anything calling this cluster’s API without a console session needs one.'}
          </p>
        ) : null}

        {rows.length ? (
          <div className="sy-table-wrap">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>App</th>
                  <th>Description</th>
                  <th>Scope</th>
                  <th>Capabilities</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  // token_id, never the row index: the legacy '#' column was
                  // page arithmetic, and reordering would reuse keys.
                  <tr key={t.token_id}>
                    <td>{t.app_name || <span className="muted">unnamed</span>}</td>
                    <td className="sy-desc" data-empty={t.description ? undefined : ''}>
                      {t.description || 'no description'}
                    </td>
                    <td className="mono">{t.scope_id}</td>
                    <td>
                      {t.capabilities.length ? (
                        <span className="sy-pills">
                          {t.capabilities.map((c) => (
                            <span key={c} className="sy-pill" data-on="">
                              {c}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span className="muted">none</span>
                      )}
                    </td>
                    <td>
                      <div className="sy-controls">
                        <Button
                          variant="ghost"
                          size="sm"
                          tone="warn"
                          aria-label={`Delete ${t.app_name || t.token_id}`}
                          onPress={() => { remove.reset(); setConfirmDelete(t) }}
                        >
                          <Icon icon={Trash2} size={13} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {total > 0 ? (
          <Pagination
            label="Token pagination"
            page={pageNo}
            pageSize={pageSize}
            total={total}
            busy={list.isFetching}
            unit="tokens"
            onPageChange={setPageNo}
            onPageSizeChange={(n) => {
              setPageSize(n)
              setPageNo(1) // row 51 does not exist at a larger page size
            }}
          />
        ) : null}

        {remove.error ? (
          <p className="sy-error" role="alert">
            {remove.error.message}
          </p>
        ) : null}

        {confirmDelete ? (
          <ConfirmStrip
            label={`Delete ${confirmDelete.app_name}?`}
            message={
              <>
                Delete <b>{confirmDelete.app_name || confirmDelete.token_id}</b>? Anything still
                calling the API with it starts failing immediately, and it cannot be restored.
              </>
            }
            confirmLabel="Delete"
            cancelLabel="Keep"
            tone="warn"
            busy={remove.isPending}
            onCancel={() => setConfirmDelete(null)}
            onConfirm={() =>
              remove.mutate(
                {
                  clusterId,
                  // The ROW's scope. A token may live outside the scope being
                  // viewed, and the route's scope would delete nothing (or,
                  // worse, something else).
                  scopeId: confirmDelete.scope_id,
                  tokenId: confirmDelete.token_id,
                },
                { onSuccess: () => setConfirmDelete(null) },
              )
            }
          />
        ) : null}

        <p className="sy-help">
          A token row carries no created-at, last-used, or expiry — the API does not return them,
          so this screen cannot tell you which tokens are stale. Tracked as a backend gap.
        </p>
      </section>

      <TokenForm
        open={formOpen}
        scopes={scopes}
        defaultScopeId={scopeId}
        busy={create.isPending}
        error={create.error?.message ?? null}
        onClose={() => setFormOpen(false)}
        onSubmit={(input) =>
          create.mutate(
            { clusterId, ...input },
            {
              onSuccess: (value) => {
                setFormOpen(false)
                setSecret({ value, appName: input.appName })
              },
            },
          )
        }
      />

      <TokenSecret
        secret={secret?.value ?? null}
        appName={secret?.appName ?? ''}
        onDismiss={() => setSecret(null)}
      />
    </div>
  )
}

export const Route = createFileRoute('/$clusterId/$scopeId/settings/tokens')({
  component: TokensPage,
})
